/**
 * `TasksSource` over the T1 api (`tasks/T-T1.md` §contract).
 *
 * Structurally typed against a narrow **port**, like the item card's and the table view's
 * sources: this module never imports server code, and a renamed procedure touches exactly one
 * function here.
 *
 * Five of the eight members are a straight `tasks.*` call. The other three exist because the
 * Tasks page edits cells on boards it does not otherwise open:
 *
 *   `itemContext`  one `boards.read`, narrowed to the row's item, for the columns, their
 *                  settings and the item's **real** cell values (see `types.ts`).
 *   `setCell` / `bulkSetCell`  `cells.set` / `cells.bulkSet` — the same service the grid writes
 *                  through, so permissions, the activity row, the outbox and the Tasks
 *                  maintenance itself (`cells.ts` step 7b) all apply unchanged.
 */
import { TASK_COLUMN_WIDTHS_KEY } from './column-widths';
import type {
  BulkCellWriteResultView,
  TaskArchiveResultView,
  TaskColumnWidthsView,
  TaskCountsView,
  TaskFacetsView,
  TaskGroupPageInput,
  TaskGroupPageView,
  TaskGroupedListInput,
  TaskItemContext,
  TaskListByGroupResultView,
  TaskListInput,
  TaskListResultView,
  TaskMappingInput,
  TaskMappingOptionsView,
  TaskMappingView,
  TaskPersonView,
  TaskSettingsView,
  TasksSource,
} from './types';

interface Query<TIn, TOut> {
  query(input: TIn): Promise<TOut>;
}
interface Mutate<TIn, TOut> {
  mutate(input: TIn): Promise<TOut>;
}

/** What `boards.read` answers, reduced to the parts this module reads. */
interface BoardReadPayloadLike {
  columns: {
    id: string;
    type: string;
    title: string;
    settingsStr: Record<string, unknown>;
    position: string;
    width: number | null;
    revision: string;
  }[];
  groups: {
    items: { id: string; columnValues: Record<string, { value: unknown; text: string | null }> }[];
  }[];
}

export interface TasksTrpc {
  tasks: {
    list: Query<TaskListInput, TaskListResultView>;
    /** P-318 · the per-group shape, beside the flat one (`specs/M10-tasks.md` §5.4). */
    listByGroup: Query<TaskGroupedListInput, TaskListByGroupResultView>;
    listGroup: Query<TaskGroupPageInput, TaskGroupPageView>;
    counts: Query<undefined, TaskCountsView>;
    facets: Query<{ tab: string; includeArchived: boolean }, TaskFacetsView>;
    settings: {
      get: Query<undefined, TaskSettingsView>;
      set: Mutate<Partial<TaskSettingsView>, TaskSettingsView>;
    };
    nudge: Mutate<{ itemId: string; assigneeId: string; message?: string }, { updateId: string }>;
    /** P-301 · manual archive. Item ids in, rows changed and items refused out. */
    archive: Mutate<{ itemIds: string[] }, TaskArchiveResultView>;
    unarchive: Mutate<{ itemIds: string[] }, TaskArchiveResultView>;
    mapping: {
      get: Query<{ boardId: string }, TaskMappingView>;
      /** P-294 — the Preferences mapping panel's two calls. */
      options: Query<{ boardId: string }, TaskMappingOptionsView>;
      set: Mutate<TaskMappingInput, TaskMappingView>;
    };
  };
  boards: {
    read: Query<Record<string, unknown>, BoardReadPayloadLike>;
    members: Query<{ id: string }, { id: string; name: string; photoUrl: string | null }[]>;
  };
  cells: {
    set: Mutate<{ boardId: string; itemId: string; columnId: string; value: unknown }, unknown>;
    bulkSet: Mutate<{ boardId: string; itemIds: string[]; columnId: string; value: unknown }, unknown>;
  };
  /**
   * **P-305** — the per-user column widths. `getAll` because the procedure answers the whole
   * merged blob (there is no `preferences.get`), `setMany` because that is the write the card
   * names and because it is the atomic one: one call, one transaction, however many keys.
   */
  preferences: {
    getAll: Query<undefined, Record<string, unknown>>;
    setMany: Mutate<{ entries: Record<string, unknown> }, Record<string, unknown>>;
  };
}

/**
 * The widths out of a preferences answer, or `{}`.
 *
 * Two shapes, because the two procedures answer differently: `preferences.getAll` hands back the
 * merged blob, while `preferences.setMany` hands back the service's whole `MutationResult` —
 * `{ data, events }` — since, unlike its neighbours in `routers/views.ts`, it does not unwrap
 * `.data`. Reading the envelope here is a one-line client concern; reshaping a procedure the
 * shell's own transport also writes through is not this card's, so the shape is accepted rather
 * than changed.
 *
 * Defensive on top of that: a browser a release behind is the normal case for a value this page
 * writes on every pointer-up, and a missing key is `{}`, never a throw.
 */
function widthsOf(answer: unknown): TaskColumnWidthsView {
  const isRecord = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);
  if (!isRecord(answer)) return {};
  const envelope = isRecord(answer['data']) ? answer['data'] : answer;
  const raw = envelope[TASK_COLUMN_WIDTHS_KEY];
  return isRecord(raw) ? (raw as TaskColumnWidthsView) : {};
}

/**
 * How many rows per group `itemContext` is willing to look through.
 *
 * The read is already narrowed by the item's own **name** (`boards.read({ search })` matches
 * the name and every text shadow, `M1-B4` §8.9), so this is the ceiling for "a board with
 * several hundred identically named rows", not for the board.
 */
export const ITEM_CONTEXT_PAGE_SIZE = 200;

export function createTasksSource(trpc: TasksTrpc): TasksSource {
  return {
    list: (input) => trpc.tasks.list.query(input),
    listByGroup: (input) => trpc.tasks.listByGroup.query(input),
    listGroup: (input) => trpc.tasks.listGroup.query(input),
    counts: () => trpc.tasks.counts.query(undefined),
    facets: (input) => trpc.tasks.facets.query(input),
    settings: () => trpc.tasks.settings.get.query(undefined),
    saveSettings: (patch) => trpc.tasks.settings.set.mutate(patch),
    nudge: (input) => trpc.tasks.nudge.mutate(input),
    mapping: (input) => trpc.tasks.mapping.get.query(input),
    archiveTasks: (input) => trpc.tasks.archive.mutate(input),
    unarchiveTasks: (input) => trpc.tasks.unarchive.mutate(input),
    mappingOptions: (input) => trpc.tasks.mapping.options.query(input),
    setMapping: (input) => trpc.tasks.mapping.set.mutate(input),

    async itemContext({ boardId, itemId, itemName }): Promise<TaskItemContext> {
      const [board, members] = await Promise.all([
        trpc.boards.read.query({
          boardId,
          // `search` is capped at 255 by the router; a longer name still matches on its head.
          search: itemName.slice(0, 255),
          pageSize: ITEM_CONTEXT_PAGE_SIZE,
          withSummaries: false,
        }),
        trpc.boards.members.query({ id: boardId }),
      ]);
      const people: TaskPersonView[] = members.map((member) => ({
        id: String(member.id),
        name: member.name ?? null,
        photoUrl: member.photoUrl ?? null,
      }));
      const columns = board.columns.map((column) => ({
        boardId,
        id: column.id,
        type: column.type,
        title: column.title,
        settingsStr: column.settingsStr ?? {},
        width: column.width,
        position: column.position,
        revision: column.revision,
      }));
      for (const group of board.groups) {
        for (const item of group.items) {
          if (String(item.id) !== String(itemId)) continue;
          const values: Record<string, unknown> = {};
          for (const [columnId, cell] of Object.entries(item.columnValues ?? {})) {
            values[columnId] = cell?.value ?? null;
          }
          return { found: true, columns, values, people };
        }
      }
      /*
       * Not found is a real answer, not an error: the row may have been archived, moved or
       * renamed since the list was drawn, and a column the caller may not *view* is absent from
       * `columnValues` by design (F8 D1). The row then offers the item card instead of an
       * editor that would write a value it never read.
       */
      return { found: false, columns, values: {}, people };
    },

    /*
     * P-305 · the list's own widths, read with the rest of this user's preferences and written
     * back under the one key. The **answer** is what the page then holds, not the value it sent:
     * the server clamps (`PREFERENCE_SCHEMAS['tasks.columnWidths']`), and a page that kept its
     * own copy would draw a width the next reload would not.
     */
    columnWidths: async () => widthsOf(await trpc.preferences.getAll.query(undefined)),
    saveColumnWidths: async (widths) =>
      widthsOf(await trpc.preferences.setMany.mutate({ entries: { [TASK_COLUMN_WIDTHS_KEY]: widths } })),

    setCell: (input) => trpc.cells.set.mutate(input),
    /*
     * The one call whose *answer* matters: `cells.bulkSet` names the items an `assignee` board
     * left out (P-293), and the bulk bar turns them into the skipped line of its toast. The
     * client is untyped at this boundary, so the shape is asserted here, once.
     */
    bulkSetCell: async (input) =>
      (await trpc.cells.bulkSet.mutate(input)) as BulkCellWriteResultView,
  };
}
